import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  FormControl,
  FormLabel,
  InputAdornment,
  Link,
  Stack,
  TextField,
  debounce,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { EnrollmentSelectionDialog } from './components/enrollment-selection-dialog';
import { FreeCourseDialog } from './components/free-course-dialog';
import { formValidationSchema, type FormValidationSchema } from './schema';
import { FieldWrapper } from '@/components/shared/field-skeleton';
import { MoneyInput } from '@/components/shared/money-input';
import { useOfferFilters } from '@/data/catalog/offer';
import { useCatalogSearch } from '@/data/catalog/search';
import { useInstitutionsSearch } from '@/data/institutions';
import type { Course } from '@/models/catalog/course';
import { useRefundRequestNavigation } from '@/packages/subsidy/pages/refund-request/steps/resource-description/navigation/use-navigation';
import { RefundRequestWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/resource-description/steps/step-ids';
import { mountCourseOptions, type CourseOption } from '@/utils/course-options';
import { formatOfferFiltersParams } from '@/utils/format-offer-filter-params';
import {
  mountCitiesOptions,
  mountHubsOptions,
  mountPeriodsOptions,
  mountStatesOptions,
  type SelectOption,
} from '@/utils/offer-filters';

const debounceSearchMs = 500;

interface Institution {
  id: number;
  name: string;
  about?: string | null;
  logo_url?: string | null;
  logo_background_color?: string | null;
  value?: string;
}

const notFoundInstitutionOption: Institution = {
  name: 'Não encontrei a instituição',
  value: 'not-found',
  about: '',
  logo_url: '',
  logo_background_color: '',
  id: 0,
};

const notFoundCourseOption: CourseOption = {
  id: 0,
  label: 'Não encontrei o curso',
  offerId: 0,
  courseType: { type: '', label: '' },
  modality: { type: '', label: '' },
  rawCourse: {} as Course,
};

const defaultFormValues = {
  institution: null as Institution | null,
  course: null as CourseOption | null,
  courseType: '',
  modality: '',
  state: null as SelectOption | null,
  city: null as SelectOption | null,
  campus: null as SelectOption<number> | null,
  period: null as SelectOption | null,
  amount: null as number | null,
  link: '',
  requestReason: '',
  duration: null as number | null,
  durationType: '',
  salesType: '',
  offerIdentifier: null as string | null,
  disableDuration: false,
};

const filter = createFilterOptions<Institution>();
const stepId = RefundRequestWizardStepIds.CatalogForm;

export const CatalogForm = () => {
  const { handleBack, canGoBack, handleNext, values } =
    useRefundRequestNavigation({
      stepId,
    });

  const courseValues = values[stepId];

  const [openEnrollmentDialog, setOpenEnrollmentDialog] = useState(false);
  const [openFreeCourseDialog, setOpenFreeCourseDialog] = useState(false);

  const [searchCourseTerm, setSearchCourseTerm] = useState<string | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Course | null>(
    null
  );
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  const [statesOptions, setStatesOptions] = useState<SelectOption[]>([]);
  const [citiesOptions, setCitiesOptions] = useState<SelectOption[]>([]);
  const [hubsOptions, setHubsOptions] = useState<SelectOption<number>[]>([]);
  const [periodsOptions, setPeriodsOptions] = useState<SelectOption[]>([]);

  const { institutions, isFetching: institutionsLoading } =
    useInstitutionsSearch();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: defaultFormValues,
    mode: 'onChange',
    resolver: zodResolver(formValidationSchema),
  });

  const watchedValues = watch();
  const { institution, course, state, city, campus, period, salesType } =
    watchedValues;

  const offerSearchFilters: Record<string, string[]> = {};
  if (campus) offerSearchFilters.hub = [campus.value.toString()];
  if (state) offerSearchFilters.location = [state.value?.toString() ?? '', ''];
  if (city)
    offerSearchFilters.location = [
      state?.value?.toString() ?? '',
      city.value?.toString() ?? '',
    ];
  if (period) offerSearchFilters.period = [period.value.toString()];

  const { catalog, isFetching: fetchingCatalog } = useCatalogSearch(
    {
      filters: {
        institutions: institution ? [institution.id] : [],
        terms: searchCourseTerm ?? '',
      },
    },
    !!searchCourseTerm && !!institution
  );

  const { data: offerFilters, isFetching: fetchingOfferFilters } =
    useOfferFilters({
      courseId: course?.id ?? 0,
      enabled: !selectedEnrollment && !!course?.id,
      queryParams: formatOfferFiltersParams({
        courseId: course?.id,
        ...offerSearchFilters,
      }),
    });

  const debounceCatalogSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchCourseTerm(value);
      }, debounceSearchMs),
    []
  );

  useEffect(() => {
    if (courseValues) {
      reset(courseValues);
    }
  }, [courseValues, reset]);

  useEffect(() => {
    if (!fetchingOfferFilters && offerFilters?.filters) {
      const {
        cheapest_offer,
        filters: { states, cities, periods, hubs },
      } = offerFilters;

      if (states?.length) setStatesOptions(mountStatesOptions(states));
      if (cities?.length) setCitiesOptions(mountCitiesOptions(cities));
      if (hubs?.length) setHubsOptions(mountHubsOptions(hubs));
      if (periods?.length) setPeriodsOptions(mountPeriodsOptions(periods));

      const allFiltersSelected = state && city && campus && period;
      if (allFiltersSelected && cheapest_offer) {
        const { duration, hash_key, pricing_description } = cheapest_offer;

        setValue(
          'amount',
          pricing_description.sale_price ?? getValues('amount')
        );
        setValue('duration', duration.quantity);
        setValue('durationType', duration.type);
        setValue('offerIdentifier', hash_key ?? null);
        setValue('salesType', pricing_description.type);
      } else if (!allFiltersSelected) {
        setValue('amount', null);
        setValue('duration', null);
        setValue('durationType', '');
        setValue('offerIdentifier', null);
        setValue('salesType', '');
      }
    }
  }, [
    offerFilters,
    fetchingOfferFilters,
    state,
    city,
    campus,
    period,
    setValue,
    getValues,
  ]);

  const courseOptions = useMemo(() => {
    if (fetchingCatalog) return [];
    if (searchCourseTerm && Array.isArray(catalog) && catalog.length === 0) {
      return [notFoundCourseOption as CourseOption];
    }
    return mountCourseOptions(catalog);
  }, [fetchingCatalog, searchCourseTerm, catalog]);

  const allOfferFiltersSelected = useMemo(
    () => Boolean(state && city && campus && period),
    [state, city, campus, period]
  );

  const getErrorMessage = (
    error: { message?: string } | undefined
  ): string | undefined => error?.message;

  const onSubmit = (data: FormValidationSchema) => {
    if (data.amount === 0) {
      setOpenFreeCourseDialog(true);
      return;
    }

    handleNext({ [stepId]: data });
  };

  const resetFormKeeping = (fieldsToKeep: Partial<FormValidationSchema>) => {
    const cleanedFields = Object.fromEntries(
      Object.entries(fieldsToKeep).map(([key, value]) => [
        key,
        value === null ? undefined : value,
      ])
    );

    reset(
      { ...defaultFormValues, ...cleanedFields },
      { keepDefaultValues: true }
    );
  };

  const handleEnrollmentSelect = (subscription: Course | null) => {
    if (!subscription) {
      setSelectedEnrollment(null);
      return;
    }

    setSelectedEnrollment(subscription);
    reset(
      {
        ...defaultFormValues,
        institution: {
          name: subscription.institution?.name ?? '',
          id: subscription.institution?.id ?? 0,
        },
        course: {
          id: subscription.id,
          offerId: subscription.offer_id,
          label: subscription.course_name,
          courseType: subscription.course_type ?? { type: '', label: '' },
          modality: subscription.modalities[0] ?? { type: '', label: '' },
          rawCourse: subscription,
        },
        courseType: subscription.course_type?.type ?? '',
        modality: subscription.modalities[0]?.type ?? '',
        state: {
          label: subscription.offer_state,
          value: subscription.offer_state,
        },
        city: {
          label: subscription.offer_city,
          value: subscription.offer_city,
        },
        campus: {
          label: subscription.institution?.name ?? '',
          value: subscription.offer_hub,
        },
        period: {
          label: subscription.offer_period,
          value: subscription.offer_period,
        },
        duration: subscription.duration.quantity,
        durationType: subscription.duration.type,
        salesType: subscription.price_type,
        offerIdentifier: subscription.hash_key ?? null,
        amount: subscription.price_with_discount ?? subscription.original_price,
      },
      { keepDefaultValues: true }
    );
    setAutocompleteKey((prev) => prev + 1);
    setOpenEnrollmentDialog(false);
  };

  const isLoading = institutionsLoading;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <FieldWrapper loading={isLoading}>
            <FormControl error={!!errors.institution} fullWidth>
              <Stack spacing={1}>
                <FormLabel id="institution-label" htmlFor="institution">
                  Instituição de ensino
                </FormLabel>
                <Controller
                  control={control}
                  name="institution"
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      {...field}
                      key={`institution-${autocompleteKey}`}
                      id="institution"
                      options={
                        institutions?.length === 0
                          ? [notFoundInstitutionOption]
                          : [...(institutions ?? []), notFoundInstitutionOption]
                      }
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);
                        if (filtered.length === 0 && params.inputValue !== '') {
                          return [notFoundInstitutionOption];
                        }
                        return filtered;
                      }}
                      getOptionLabel={(option) => option?.name ?? ''}
                      getOptionKey={(option) => option.id}
                      isOptionEqualToValue={(a, b) => a?.id === b?.id}
                      onChange={(_, option, reason) => {
                        setSelectedEnrollment(null);
                        if (
                          (option as Institution)?.value === 'not-found' ||
                          option?.id === 0
                        ) {
                          setOpenEnrollmentDialog(true);
                          return;
                        }

                        if (reason === 'clear') {
                          reset(defaultFormValues);
                        } else if (reason === 'selectOption' && option) {
                          resetFormKeeping({
                            institution: option as Institution,
                          });
                        }
                      }}
                      openOnFocus
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!error}
                          helperText={getErrorMessage(error)}
                          placeholder="Selecione"
                        />
                      )}
                    />
                  )}
                />
              </Stack>
            </FormControl>
          </FieldWrapper>

          <FieldWrapper loading={isLoading}>
            <FormControl error={!!errors.course} fullWidth>
              <Stack spacing={1}>
                <FormLabel id="course-label" htmlFor="course">
                  Curso
                </FormLabel>
                <Controller
                  control={control}
                  name="course"
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      {...field}
                      key={`course-${autocompleteKey}`}
                      id="course"
                      disabled={!institution}
                      filterOptions={(x) => x}
                      getOptionLabel={(option) => option?.label ?? ''}
                      isOptionEqualToValue={(a, b) =>
                        a?.id === b?.id || a?.offerId === b?.offerId
                      }
                      loading={fetchingCatalog && !!searchCourseTerm}
                      loadingText="Carregando..."
                      noOptionsText={
                        !searchCourseTerm
                          ? 'Digite o nome de um curso'
                          : 'Não encontramos nenhum curso com esses parâmetros.'
                      }
                      onBlur={() => setSearchCourseTerm(null)}
                      onChange={(_, option, reason) => {
                        setSelectedEnrollment(null);

                        if (option?.id === 0) {
                          setOpenEnrollmentDialog(true);
                          return;
                        }

                        if (reason === 'clear') {
                          resetFormKeeping({
                            institution: institution || undefined,
                          });
                          return;
                        }

                        if (reason === 'selectOption' && option) {
                          const fullOption = option;
                          resetFormKeeping({
                            institution: institution || undefined,
                            course: option,
                            courseType: fullOption.courseType?.type,
                            modality: fullOption.modality?.type,
                          });
                        }
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') {
                          debounceCatalogSearchTerm(value);
                        }
                      }}
                      openOnFocus
                      options={courseOptions}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!error}
                          helperText={getErrorMessage(error)}
                          placeholder="Selecione"
                        />
                      )}
                      value={course}
                    />
                  )}
                />
              </Stack>
            </FormControl>
          </FieldWrapper>

          {/* Estado e Cidade */}
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <Box flex={1}>
              <FieldWrapper
                loading={isLoading || (fetchingOfferFilters && !state)}
              >
                <FormControl error={!!errors.state} fullWidth>
                  <Stack spacing={1}>
                    <FormLabel id="state-label" htmlFor="state">
                      Estado
                    </FormLabel>
                    <Controller
                      control={control}
                      name="state"
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          id="state"
                          disabled={!course}
                          options={statesOptions}
                          getOptionLabel={(option) => option?.label ?? ''}
                          isOptionEqualToValue={(a, b) => a?.value === b?.value}
                          noOptionsText="Sem opções de estado"
                          onChange={(_, option, reason) => {
                            setSelectedEnrollment(null);
                            const keepFields = {
                              institution: institution || undefined,
                              course: course || undefined,
                              courseType: getValues('courseType'),
                              modality: getValues('modality'),
                            };
                            if (reason === 'clear') {
                              resetFormKeeping(keepFields);
                            } else if (reason === 'selectOption') {
                              resetFormKeeping({
                                ...keepFields,
                                state: option || undefined,
                              });
                            }
                          }}
                          openOnFocus
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!error}
                              helperText={getErrorMessage(error)}
                              placeholder="Selecione"
                            />
                          )}
                          value={state}
                        />
                      )}
                    />
                  </Stack>
                </FormControl>
              </FieldWrapper>
            </Box>

            <Box flex={1}>
              <FieldWrapper
                loading={isLoading || (fetchingOfferFilters && !city)}
              >
                <FormControl error={!!errors.city} fullWidth>
                  <Stack spacing={1}>
                    <FormLabel id="city-label" htmlFor="city">
                      Cidade
                    </FormLabel>
                    <Controller
                      control={control}
                      name="city"
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          id="city"
                          disabled={!state}
                          options={citiesOptions}
                          getOptionLabel={(option) => option?.label ?? ''}
                          isOptionEqualToValue={(a, b) => a?.value === b?.value}
                          noOptionsText="Sem opções de cidade"
                          onChange={(_, option, reason) => {
                            setSelectedEnrollment(null);
                            const keepFields = {
                              institution: institution || undefined,
                              course: course || undefined,
                              courseType: getValues('courseType'),
                              modality: getValues('modality'),
                              state: state || undefined,
                            };
                            if (reason === 'clear') {
                              resetFormKeeping(keepFields);
                            } else if (reason === 'selectOption') {
                              resetFormKeeping({
                                ...keepFields,
                                city: option || undefined,
                              });
                            }
                          }}
                          openOnFocus
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!error}
                              helperText={getErrorMessage(error)}
                              placeholder="Selecione"
                            />
                          )}
                          value={city}
                        />
                      )}
                    />
                  </Stack>
                </FormControl>
              </FieldWrapper>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <Box flex={1}>
              <FieldWrapper
                loading={isLoading || (fetchingOfferFilters && !campus)}
              >
                <FormControl error={!!errors.campus} fullWidth>
                  <Stack spacing={1}>
                    <FormLabel id="campus-label" htmlFor="campus">
                      Polo
                    </FormLabel>
                    <Controller
                      control={control}
                      name="campus"
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          id="campus"
                          disabled={!city}
                          options={hubsOptions}
                          getOptionLabel={(option) => option?.label ?? ''}
                          isOptionEqualToValue={(a, b) => a?.value === b?.value}
                          noOptionsText="Sem opções de polo"
                          onChange={(_, option, reason) => {
                            setSelectedEnrollment(null);
                            const keepFields = {
                              institution: institution || undefined,
                              course: course || undefined,
                              courseType: getValues('courseType'),
                              modality: getValues('modality'),
                              state: state || undefined,
                              city: city || undefined,
                            };
                            if (reason === 'clear') {
                              resetFormKeeping(keepFields);
                            } else if (reason === 'selectOption') {
                              resetFormKeeping({
                                ...keepFields,
                                campus: option || undefined,
                              });
                            }
                          }}
                          openOnFocus
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!error}
                              helperText={getErrorMessage(error)}
                              placeholder="Selecione"
                            />
                          )}
                          value={campus}
                        />
                      )}
                    />
                  </Stack>
                </FormControl>
              </FieldWrapper>
            </Box>

            <Box flex={1}>
              <FieldWrapper
                loading={isLoading || (fetchingOfferFilters && !period)}
              >
                <FormControl error={!!errors.period} fullWidth>
                  <Stack spacing={1}>
                    <FormLabel id="period-label" htmlFor="period">
                      Turno
                    </FormLabel>
                    <Controller
                      control={control}
                      name="period"
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          id="period"
                          disabled={!campus}
                          options={periodsOptions}
                          getOptionLabel={(option) => option?.label ?? ''}
                          isOptionEqualToValue={(a, b) => a?.value === b?.value}
                          noOptionsText="Sem opções de turno"
                          onChange={(_, option, reason) => {
                            setSelectedEnrollment(null);
                            const keepFields = {
                              institution: institution || undefined,
                              course: course || undefined,
                              courseType: getValues('courseType'),
                              modality: getValues('modality'),
                              state: state || undefined,
                              city: city || undefined,
                              campus: campus || undefined,
                            };
                            if (reason === 'clear') {
                              resetFormKeeping(keepFields);
                            } else if (reason === 'selectOption') {
                              resetFormKeeping({
                                ...keepFields,
                                period: option || undefined,
                              });
                            }
                          }}
                          openOnFocus
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!error}
                              helperText={getErrorMessage(error)}
                              placeholder="Selecione"
                            />
                          )}
                          value={period}
                        />
                      )}
                    />
                  </Stack>
                </FormControl>
              </FieldWrapper>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <Box flex={1}>
              <FieldWrapper
                loading={
                  isLoading ||
                  (!watchedValues.amount &&
                    allOfferFiltersSelected &&
                    fetchingOfferFilters)
                }
              >
                <FormControl error={!!errors.amount} fullWidth>
                  <Stack spacing={1}>
                    <FormLabel id="amount-label" htmlFor="amount">
                      {salesType === 'tuition'
                        ? 'Valor da mensalidade'
                        : 'Custo total do item'}{' '}
                      (R$)
                    </FormLabel>
                    <Controller
                      control={control}
                      name="amount"
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          id="amount"
                          disabled={!period}
                          error={!!error}
                          helperText={getErrorMessage(error)}
                          value={field.value ?? ''}
                          slotProps={{
                            input: {
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              inputComponent: MoneyInput as any,
                              startAdornment: (
                                <InputAdornment position="start">
                                  R$
                                </InputAdornment>
                              ),
                            },
                          }}
                          placeholder="0,00"
                        />
                      )}
                    />
                  </Stack>
                </FormControl>
              </FieldWrapper>
            </Box>
            <Box flex={1} display={{ xs: 'none', md: 'block' }} />
          </Stack>

          <FieldWrapper loading={isLoading}>
            <FormControl error={!!errors.link} fullWidth>
              <Stack spacing={1}>
                <FormLabel id="link-label" htmlFor="link">
                  Link do item
                  <Box
                    component="span"
                    sx={{ pl: 1, fontWeight: 400, fontSize: '12px' }}
                  >
                    opcional
                  </Box>
                </FormLabel>
                <Controller
                  control={control}
                  name="link"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      id="link"
                      error={!!error}
                      helperText={getErrorMessage(error)}
                      placeholder="Ex.: www.link.com"
                    />
                  )}
                />
              </Stack>
            </FormControl>
          </FieldWrapper>

          <FieldWrapper loading={isLoading}>
            <FormControl error={!!errors.requestReason} fullWidth>
              <Stack spacing={1}>
                <FormLabel id="requestReason-label" htmlFor="requestReason">
                  Descrição ou justificativa
                  <Box
                    component="span"
                    sx={{ pl: 1, fontWeight: 400, fontSize: '12px' }}
                  >
                    opcional
                  </Box>
                </FormLabel>
                <Controller
                  control={control}
                  name="requestReason"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      id="requestReason"
                      error={!!error}
                      helperText={getErrorMessage(error)}
                      multiline
                      rows={4}
                      placeholder="Descreva o motivo da compra para avaliação da sua empresa."
                    />
                  )}
                />
              </Stack>
            </FormControl>
          </FieldWrapper>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={4}
        >
          <Link
            component="button"
            type="button"
            fontSize={14}
            onClick={() => setOpenEnrollmentDialog(true)}
            sx={{ cursor: 'pointer' }}
          >
            {selectedEnrollment
              ? 'Alterar inscrição'
              : 'Não encontrei o meu curso'}
          </Link>

          <Stack direction="row" gap={2}>
            {canGoBack && (
              <Button variant="text" onClick={handleBack}>
                Voltar
              </Button>
            )}
            <Button type="submit" variant="contained">
              Próximo
            </Button>
          </Stack>
        </Stack>
      </form>

      {openEnrollmentDialog && (
        <EnrollmentSelectionDialog
          value={selectedEnrollment}
          onChange={setSelectedEnrollment}
          onClose={() => setOpenEnrollmentDialog(false)}
          onSelect={handleEnrollmentSelect}
        />
      )}
      {openFreeCourseDialog && (
        <FreeCourseDialog onClose={() => setOpenFreeCourseDialog(false)} />
      )}
    </>
  );
};
